import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch } from "react-redux";
import { logOut } from "../../auth/authActions";

import MediaBrowser from "../../creator-application/common/MediaBrowser";
import ModuleGroupTable from "./ModuleGroupTable";
import { useModuleGroup } from "../moduleGroupsService";
import SearchBar from "../../creator-application/common/SearchBar";
import TotalsHeader from "../../creator-application/common/TotalsHeader";

import AddModuleDialog from "./AddModuleDialog";
import { useParams } from "react-router-dom";
import { filter, test, join } from "ramda";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      display: "flex",
      flexDirection: "column",
      height: "95%",
      padding: "0",
      borderRadius: "20px",
      border: "3px white solid",
    },

    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    count: {
      marginRight: 10,
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 275px)",
      overflow: "auto",
    },

    filters: {
      minWidth: 650,
      width: "auto",
      paddingTop: 3,
      marginLeft: theme.spacing(2),
    },
  }));

const ModuleGroupEdit = ({ onBack }) => {
  const { id } = useParams();

  const [scrollVisible, setScrollVisible] = useState(true);
  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const classes = useStyles(scrollVisible)();

  const reduxDispatch = useDispatch();

  const [addModuleDialogOpen, setAddModuleDialogOpen] = useState(false);

  const [moduleGroup, setModuleGroup] = useState({
    modules: [],
    filteredModules: [],
  });
  const initialModuleGroup = useModuleGroup(id);
  useEffect(() => {
    if (initialModuleGroup.isError && initialModuleGroup.error.status === 401) {
      reduxDispatch(logOut());
    }

    if (initialModuleGroup.isSuccess) {
      setModuleGroup({
        ...initialModuleGroup.data,
        filteredModules: initialModuleGroup.data.modules,
      });
      setScrollVisible(true);
    }
  }, [
    initialModuleGroup.isError,
    initialModuleGroup.error,
    reduxDispatch,
    initialModuleGroup.isSuccess,
    initialModuleGroup.data,
    initialModuleGroup.isLoading,
  ]);

  // TODO: fix
  // The pagination and offset from API is not working as intended.
  // When I set param as ?limit=10, API returned 2 module groups even though more exist
  // And there is a version issue with MUI TablePagination where onPageChange returns undefined for newPage
  // So this is my hacky solution for now
  // it works as intended but is not elegant
  const [page, setPage] = useState(0);
  const handleChangePage = (event, newPage) => {
    event.currentTarget.attributes["aria-label"].value === "Next page"
      ? setPage(page + 1)
      : setPage(page - 1);
  };

  const [appliedQuery, setAppliedQuery] = useState([]);
  const handleQueryChange = updatedQuery => {
    setAppliedQuery(updatedQuery);
    const queries = new RegExp(join("|", updatedQuery), "ig");
    const filteredModules = filter(
      module => test(queries, module.moduleName),
      initialModuleGroup.data.modules,
    );
    setModuleGroup(prevModuleGroup => ({
      ...prevModuleGroup,
      filteredModules: filteredModules,
    }));
  };

  const handleModuleRemove = () => {
    setAppliedQuery([]);
    initialModuleGroup.refetch();
  };

  return (
    <Container
      id="module-group-edit-browser"
      maxWidth={false}
      classes={{ root: classes.root }}
    >
      <AddModuleDialog
        initialModuleGroup={initialModuleGroup}
        open={addModuleDialogOpen}
        onCancel={() => setAddModuleDialogOpen(false)}
        onOk={() => {
          setAddModuleDialogOpen(false);
          initialModuleGroup.refetch();
        }}
      />
      <MediaBrowser
        loaded={initialModuleGroup.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <SearchBar query={appliedQuery} onQueryChange={handleQueryChange} />
        }
        totalsHeader={
          <TotalsHeader
            title={moduleGroup.moduleGroupName}
            countText={"module"}
            count={
              initialModuleGroup.isSuccess
                ? moduleGroup.filteredModules.length
                : "-"
            }
            selectedActions={
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    classes={{ root: classes.addNewButton }}
                    onClick={() => setAddModuleDialogOpen(true)}
                  >
                    Add Module
                  </Button>
                </Grid>
              </Grid>
            }
          />
        }
        mediaTable={
          <ModuleGroupTable
            initialModuleGroup={moduleGroup}
            onModuleRemove={handleModuleRemove}
            total={
              initialModuleGroup.isSuccess
                ? moduleGroup.filteredModules.length
                : 0
            }
            page={page}
            rowsPerPage={10}
            onPageChange={handleChangePage}
            onScrollVisible={handleScrollVisible}
          />
        }
      />
    </Container>
  );
};

export default ModuleGroupEdit;
